import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseSortableDataProps {
  data: any[];
  defaultSortKey?: string;
  currentPage?: number;
  filters?: any;
}

export interface SelectedSort {
  key: string;
  direction: 'default' | 'asc' | 'desc';
}

const getComparableValue = (value: any): string | number => {
  if (value && typeof value === 'object') return value.name ?? '';
  return value;
};

const useSortableData = ({
  data = [],
  defaultSortKey = '',
  currentPage,
}: UseSortableDataProps) => {
  const [selectedSort, setSelectedSort] = useState<SelectedSort>({
    key: defaultSortKey,
    direction: 'default',
  });

  useEffect(() => {
    if (currentPage && currentPage > 1) {
      setSelectedSort({
        key: defaultSortKey,
        direction: 'default',
      });
    }
  }, [currentPage, defaultSortKey]);

  const compareValues = (
    valueA: any,
    valueB: any,
    direction: 'asc' | 'desc'
  ) => {
    let comparison = 0;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      comparison = valueA - valueB;
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      const dateA = dayjs(valueA);
      const dateB = dayjs(valueB);

      if (dateA.isValid() && dateB.isValid()) {
        comparison = dateA.valueOf() - dateB.valueOf();
      } else {
        comparison = valueA.localeCompare(valueB);
      }
    } else {
      comparison = String(valueA).localeCompare(String(valueB));
    }

    return direction === 'asc' ? comparison : -comparison;
  };

  const sortedData = useMemo(() => {
    const { key, direction } = selectedSort;
    if (direction === 'default') return data;

    return [...data].sort((a, b) =>
      compareValues(getComparableValue(a[key]), getComparableValue(b[key]), direction)
    );
  }, [data, selectedSort.direction, selectedSort.key]);

  const handleSort = useCallback((key: string) => {
    setSelectedSort((prev) => ({
      key,
      direction:
        key === prev.key
          ? prev?.direction === 'default'
            ? 'asc'
            : prev?.direction === 'desc'
              ? 'default'
              : 'desc'
          : 'asc',
    }));
  }, []);

  return { sortedData, selectedSort, handleSort };
};

export default useSortableData;
